import URLS from "~/constants/urls"
import { ITokenInfo } from "~/types/global"
import { axiosInstance } from "~/utils/axios"

function tenantUserPrefix(tokenInfo: ITokenInfo) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  return `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}`
}

// A rep is watching a spinner for every one of these. axiosInstance sets no
// timeout, so a request that never settles leaves "Log call & finish" loading
// for as long as the tab is open - the await never returns, so the finally
// that clears the spinner never runs. Failing is recoverable; hanging is not.
const DIALER_WRITE_TIMEOUT_MS = 20000

export function getDialerCallSummaryStreamUrl(
  tokenInfo: ITokenInfo,
  callSid: string
): string {
  const path = `${tenantUserPrefix(tokenInfo)}/dialer/calls/summary-stream`
  return `${URLS.V1}/${path}?call_sid=${encodeURIComponent(callSid)}`
}

export async function createDialerSession(
  tokenInfo: ITokenInfo,
  isRedial = false,
  callingHours?: { start: string; end: string },
  listId?: string | number | null
): Promise<{ data: { data: { id: number } } }> {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/sessions`

  return await axiosInstance({
    method: "POST",
    url,
    data: {
      is_redial: isRedial,
      ...(listId ? { list_id: String(listId) } : {}),
      ...(callingHours
        ? {
            calling_hours_start: callingHours.start,
            calling_hours_end: callingHours.end
          }
        : {})
    },
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getActiveDialerSession(
  tokenInfo: ITokenInfo
): Promise<{ data: { data: { id: number } | null } }> {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/sessions/active`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export type ResumableSession = {
  session_id: number
  calling_hours_start: string | null
  calling_hours_end: string | null
  paused_at: string | null
  total: number
  called: number
  remaining: number
  people: { person_id: string | number }[]
  /** Everyone this list has already been dialled on. */
  called_people: {
    person_id: string | number
    outcome: "connected" | "unconnected" | "cancelled" | null
    outcome_reason: "no_answer" | "rejected" | null
    duration_seconds: number | null
    dialled_at: string | null
  }[]
}

export async function getResumableDialerSession(
  tokenInfo: ITokenInfo,
  listId: string | number
): Promise<{ data: { data: ResumableSession | null } }> {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/sessions/resumable`

  return await axiosInstance({
    method: "GET",
    url,
    params: { list_id: String(listId) },
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function resumeDialerSession(
  tokenInfo: ITokenInfo,
  sessionId: number
) {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/sessions/${sessionId}/resume`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function discardDialerListQueue(
  tokenInfo: ITokenInfo,
  listId: string | number
) {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/lists/${listId}/queue/discard`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function discardDialerSession(
  tokenInfo: ITokenInfo,
  sessionId: number
) {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/sessions/${sessionId}/discard`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function completeDialerSession(
  tokenInfo: ITokenInfo,
  sessionId: number
) {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/sessions/${sessionId}/complete`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getDialerCallByCallSid(
  tokenInfo: ITokenInfo,
  callSid: string
): Promise<{
  data: { data: { id: number; status: string; ai_summary: string | null } }
}> {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/calls/lookup`

  return await axiosInstance({
    timeout: DIALER_WRITE_TIMEOUT_MS,
    method: "GET",
    url,
    params: { call_sid: callSid },
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

// dialer_calls row shape, raw from dialer-service — name/account/list are NOT
// here (that service only owns the call, not the People OS person the id
// points at) and must be resolved separately via getDialerHistoryPeople.
export interface IDialerCall {
  id: number
  tenant_id: number
  session_id: number | null
  user_id: number
  people_id: string | null
  champion_id: string | null
  person_id: string | null
  /**
   * Which list the rep was dialing from, captured at reserve time — reflects
   * the truth at CALL time, not the person's current list membership (which
   * can change later, e.g. added to a new list after the call happened).
   */
  list_id: number | null
  outbound_number: string | null
  callee_number: string
  /**
   * Who placed the call. Its own column since V155 — inbound used to be
   * recorded as status='INBOUND', which meant an inbound call could never
   * also carry an outcome and so could never show as missed.
   */
  direction: "inbound" | "outbound"
  twilio_call_sid: string | null
  status: string
  disposition: string | null
  /**
   * Who or what picked up, as the provider's answering-machine detection
   * reported it. Only "human" is a conversation; a machine answers, runs for a
   * duration and completes exactly like one.
   */
  answered_by: string | null
  duration_seconds: number | null
  /**
   * An s3:// URI, not a playable URL — the browser cannot fetch it directly.
   * Indicates only that a recording exists; getDialerCallRecordingUrl
   * returns a signed URL for playback.
   */
  recording_url: string | null
  /**
   * An s3:// URI pointing at the stored transcript, not the transcript text.
   * getDialerCallTranscript resolves it to the actual contents.
   */
  transcript: string | null
  /**
   * Transcription state for the recording. Null on rows written before the
   * column existed.
   */
  transcript_status: "pending" | "ready" | "failed" | null
  ai_summary: string | null
  notes: string | null
  meeting_set_at: string | null
  started_at: string | null
  ended_at: string | null
  created_at: string
  updated_at: string
}

export interface IDialerHistoryCounts {
  all: number
  missed: number
}

export async function getDialerCallHistory(
  tokenInfo: ITokenInfo,
  args: {
    tab?: "all" | "missed"
    from?: string
    to?: string
    status?: string
    disposition?: string
    search?: string
    // person_ids/champion_ids/list_person_ids are resolved on the web side
    // (searchPeople / listPeople) and passed in as ids — dialer-service only
    // owns dialer_calls, not the People OS person a row's people_id/
    // champion_id points at, so it can't resolve a name/list itself.
    personIds?: string[]
    championIds?: string[]
    listPersonIds?: string[]
    minDuration?: number
    maxDuration?: number
    sortBy?: string
    sortDir?: "asc" | "desc"
    pageToken?: string | null
    limit?: number
  } = {}
): Promise<{
  data: {
    data: {
      calls: IDialerCall[]
      total: number
      counts: IDialerHistoryCounts
      next_page_token: string | null
    }
  }
}> {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/calls`

  return await axiosInstance({
    method: "GET",
    url,
    params: {
      tab: args.tab,
      from: args.from,
      to: args.to,
      status: args.status,
      disposition: args.disposition,
      search: args.search,
      person_ids: args.personIds?.length ? args.personIds.join(",") : undefined,
      champion_ids: args.championIds?.length
        ? args.championIds.join(",")
        : undefined,
      list_person_ids: args.listPersonIds?.length
        ? args.listPersonIds.join(",")
        : undefined,
      min_duration: args.minDuration,
      max_duration: args.maxDuration,
      sort_by: args.sortBy,
      sort_dir: args.sortDir,
      page_token: args.pageToken || undefined,
      limit: args.limit
    },
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function setDialerCallDisposition(
  tokenInfo: ITokenInfo,
  callId: number | string,
  disposition: string,
  meetingSetAt?: string
) {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/calls/${callId}/disposition`

  return await axiosInstance({
    timeout: DIALER_WRITE_TIMEOUT_MS,
    method: "POST",
    url,
    data: {
      disposition,
      ...(meetingSetAt ? { meeting_set_at: meetingSetAt } : {})
    },
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function setDialerCallNote(
  tokenInfo: ITokenInfo,
  callId: number | string,
  notes: string
) {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/calls/${callId}/notes`

  return await axiosInstance({
    timeout: DIALER_WRITE_TIMEOUT_MS,
    method: "POST",
    url,
    data: { notes },
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export interface IDialerQueueEntry {
  id: number
  tenant_id: number
  user_id: number
  session_id: number
  people_id: string | null
  champion_id: string | null
  person_id: string | null
  sequence: number
  status: "PENDING" | "CALLED" | "SKIPPED"
  created_at: string
  updated_at: string
}

export function entryPersonId(entry: IDialerQueueEntry): string | null {
  return entry?.person_id ?? entry?.champion_id ?? entry?.people_id ?? null
}

export async function enqueueDialerQueue(
  tokenInfo: ITokenInfo,
  sessionId: number,
  peopleIds: string[]
): Promise<{ data: { data: IDialerQueueEntry[] } }> {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/sessions/${sessionId}/queue`

  return await axiosInstance({
    method: "POST",
    url,
    data: { people_ids: peopleIds },
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getDialerQueue(
  tokenInfo: ITokenInfo,
  sessionId: number
): Promise<{ data: { data: IDialerQueueEntry[] } }> {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/sessions/${sessionId}/queue`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getNextDialerQueueEntry(
  tokenInfo: ITokenInfo,
  sessionId: number
): Promise<{ data: { data: IDialerQueueEntry | null } }> {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/sessions/${sessionId}/queue/next`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function updateDialerQueueEntryStatus(
  tokenInfo: ITokenInfo,
  sessionId: number,
  queueId: number,
  status: "PENDING" | "CALLED" | "SKIPPED"
) {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/sessions/${sessionId}/queue/${queueId}/status`

  return await axiosInstance({
    method: "POST",
    url,
    data: { status },
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function reserveDialerCall(
  tokenInfo: ITokenInfo,
  sessionId: number,
  queueId: number,
  calleeNumber: string,
  outboundNumber?: string,
  linkedinUrl?: string,
  listId?: string
): Promise<{ data: { data: { call_id: number } } }> {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/sessions/${sessionId}/queue/${queueId}/reserve-call`

  return await axiosInstance({
    method: "POST",
    url,
    data: {
      callee_number: calleeNumber,
      ...(outboundNumber ? { outbound_number: outboundNumber } : {}),
      ...(linkedinUrl ? { linkedin_url: linkedinUrl } : {}),
      ...(listId ? { list_id: listId } : {})
    },
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export interface ITalkingPoint {
  slot: string
  title: string | null
  detail: string | null
}

export async function getDialerTalkingPoints(
  tokenInfo: ITokenInfo,
  peopleId: number | string
): Promise<{
  data: {
    people_id: number | string | null
    champion_id: string | null
    generated_at: string | null
    source: string | null
    points: ITalkingPoint[]
  }
}> {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/talking-points?people_id=${encodeURIComponent(peopleId)}`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export interface IAvailableNumber {
  phone_number: string
  friendly_name: string
  locality: string | null
  region: string | null
  country: string
}

export async function searchDialerNumbers(
  tokenInfo: ITokenInfo,
  params: { country: string; contains: string; match: string }
): Promise<{
  data: {
    numbers: IAvailableNumber[]
    remaining: number
    limit_reached: boolean
    max_numbers: number
  }
}> {
  const q = new URLSearchParams({
    country: params.country,
    match: params.match
  })
  if (params.contains) q.set("contains", params.contains)
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/numbers/available?${q.toString()}`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function buyDialerNumber(
  tokenInfo: ITokenInfo,
  phoneNumber: string
): Promise<{ data: { number: { id: number; phone_number: string } } }> {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/numbers`

  return await axiosInstance({
    method: "POST",
    url,
    data: { phone_number: phoneNumber },
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getDialerCallSummary(
  tokenInfo: ITokenInfo,
  peopleId: number | string,
  callSid?: string | null
): Promise<{
  data: {
    summary: string | null
    status: "pending" | "ready" | "failed"
    generated_at: string | null
  }
}> {
  const url =
    `${tenantUserPrefix(tokenInfo)}/dialer/call-summary?people_id=${encodeURIComponent(peopleId)}` +
    (callSid ? `&call_sid=${encodeURIComponent(callSid)}` : "")

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getDialerSettings(tokenInfo: ITokenInfo): Promise<{
  data: {
    personal_number: string | null
    forward_enabled: boolean
    auto_advance: boolean
    line_count?: number
    dial_mode?: "power" | "parallel"
  }
}> {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/settings`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function updateDialerSettings(
  tokenInfo: ITokenInfo,
  payload: {
    personal_number?: string | null
    forward_enabled?: boolean
    auto_advance?: boolean
    line_count?: number
    dial_mode?: "power" | "parallel"
  }
): Promise<{
  data: {
    personal_number: string | null
    forward_enabled: boolean
    auto_advance: boolean
    line_count?: number
    dial_mode?: "power" | "parallel"
  }
}> {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/settings`

  return await axiosInstance({
    method: "PUT",
    url,
    data: payload,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

// A signed, time-limited URL for the call's recording, playable directly.
export async function getDialerCallRecordingUrl(
  tokenInfo: ITokenInfo,
  callId: number | string
): Promise<{ data: { data: { url: string; expires_in: number } } }> {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/calls/${callId}/recording`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export type IDialerTranscriptStatus = "pending" | "ready" | "failed"

export async function getDialerCallTranscript(
  tokenInfo: ITokenInfo,
  callId: number | string
): Promise<{
  data: {
    data: { transcript: string | null; status: IDialerTranscriptStatus }
  }
}> {
  const url = `${tenantUserPrefix(tokenInfo)}/dialer/calls/${callId}/transcript`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export type VoiceTemplate = {
  id: number
  name: string
  purpose: "abandoned_call" | "voicemail_drop"
  kind: "text" | "recording"
  body: string | null
  voice_id: string | null
  engine: string | null
  is_active: boolean
  has_audio: boolean
  audio_stale: boolean
  audio_rendered_at: string | null
}

export async function getVoiceTemplates(
  tokenInfo: ITokenInfo
): Promise<{ data: { templates: VoiceTemplate[] } }> {
  return await axiosInstance({
    method: "GET",
    url: `${tenantUserPrefix(tokenInfo)}/dialer/voice-templates`,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

export async function saveVoiceTemplate(
  tokenInfo: ITokenInfo,
  body: {
    id?: number
    name: string
    purpose: VoiceTemplate["purpose"]
    body: string
    voice_id: string
    engine?: string
    is_active?: boolean
  }
): Promise<{ data: { template: VoiceTemplate; warning?: string } }> {
  return await axiosInstance({
    method: "POST",
    url: `${tenantUserPrefix(tokenInfo)}/dialer/voice-templates`,
    data: body,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

export async function getVoiceTemplateAudioUrl(
  tokenInfo: ITokenInfo,
  templateId: number
): Promise<{ data: { url: string; expires_in: number } }> {
  return await axiosInstance({
    method: "GET",
    url: `${tenantUserPrefix(tokenInfo)}/dialer/voice-templates/${templateId}/audio`,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

export async function deleteVoiceTemplate(
  tokenInfo: ITokenInfo,
  templateId: number
): Promise<void> {
  return await axiosInstance({
    method: "DELETE",
    url: `${tenantUserPrefix(tokenInfo)}/dialer/voice-templates/${templateId}`,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

export type BurstLegStatus =
  | "pending"
  | "dialing"
  | "won"
  | "lost"
  | "abandoned"
  | "machine"
  | "skipped"
  | "snoozed"
  | "failed"
  | "no_answer"
  | "busy"
  | "canceled"

export type BurstLeg = {
  leg_id: number
  champion_id: string | null
  people_id: number | null
  phone_number: string | null
  status: BurstLegStatus
  rang: boolean
  answered_at: string | null
  ended_at: string | null
  dialer_call_id: number | null
  answered_by?: string | null
}

export type BurstState = {
  burst_id: number
  session_id: number | null
  requested_line_count: number | null
  governor_capped: boolean
  ended_at: string | null
  winner_leg_id: number | null
  legs: BurstLeg[]
}

export async function startBurst(
  tokenInfo: ITokenInfo,
  body: {
    contacts: {
      champion_id?: string
      people_id?: number
      phone_number: string
    }[]
    session_id?: number | null
    from_number?: string | null
    lines: number
  }
): Promise<{
  data: {
    burst_id: number | null
    lines_requested: number
    lines_queued: number
    governor?: {
      capped: boolean
      abandoned_rate_pct: number
      answered_sampled: number
      ceiling_pct: number
      reason: string | null
    }
    outside_hours: string[]
    duplicates_skipped: number
    duplicate_ids: string[]
    invalid_numbers?: string[]
  }
}> {
  return await axiosInstance({
    method: "POST",
    url: `${tenantUserPrefix(tokenInfo)}/dialer/burst`,
    data: body,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

export async function getBurst(
  tokenInfo: ITokenInfo,
  burstId: number
): Promise<{ data: BurstState }> {
  return await axiosInstance({
    method: "GET",
    url: `${tenantUserPrefix(tokenInfo)}/dialer/burst/${burstId}`,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

export async function endBurst(tokenInfo: ITokenInfo, burstId: number) {
  return await axiosInstance({
    method: "POST",
    url: `${tenantUserPrefix(tokenInfo)}/dialer/burst/${burstId}/end`,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}
